/**
 * Allergen, dietary and restricted-class disclosure (FRS-46 and FRS-38,
 * ORDR-121).
 *
 * `disclosureFor(item: unknown): string` and `MenuDisclosure(): ReactElement`
 * (no props) are FROZEN by HQ's entry stub. The sentence itself is built in
 * `disclosure.ts` from the wire keys `wire.ts` reads.
 *
 * Nothing here may ever say "free from" for an item: a missing record is
 * UNKNOWN, never absent. Only an explicit ABSENT is declared free.
 */

import { createElement, type ReactElement } from "react";
import { describeItem } from "./disclosure.js";

/**
 * The one-string disclosure for one wire menu item (endpoint #7), as the
 * device's clock, zone and language see it. `sectionsFromPublicMenu` calls
 * `describeItem` directly so the diner's language and a test's clock reach it.
 */
export function disclosureFor(item: unknown): string {
  const language = typeof navigator === "undefined" ? "en" : (navigator.language ?? "en");
  return describeItem(item, { language, now: new Date() });
}

/**
 * The standing direction (FR-DSC-002), in the notice styling `CustomerStatus`
 * uses. It says the same thing whether or not any record is missing: the data
 * is a disclosure, never a clearance. HQ ruling S1 mounts it above the menu.
 */
export function MenuDisclosure(): ReactElement {
  return createElement(
    "p",
    {
      "data-testid": "menu-disclosure",
      className: "m-3 rounded-[var(--border-radius-md)] bg-[var(--color-neutral-100)] p-3 text-sm",
    },
    "Allergen and dietary information comes from this location's records. It is a disclosure, not a guarantee. If you have a concern it does not answer, please contact the location before you order.",
  );
}
